import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth'
import { ApiFailure } from '../../api'
import { FieldError, Icon, Loading } from './shared'
import { useT } from './i18n'
import { rules } from './validate'
import '../../mobile.css'

/* Sign-in for the phone app.
 *
 * One box takes a phone number or an email (older staff accounts can still
 * use their username), proved by the password or by a one-time code sent to
 * that phone or address. Mistakes in the box are pointed out as they are
 * typed; refusals from the server are shown above the form.
 *
 * This screen sits outside the mobile router on purpose: it has to render
 * before there is a session, so it cannot use the shared store the other
 * screens read from. It keeps the phone frame and palette so signing in does
 * not throw the user into the desktop layout on the way past. */

type Method = 'password' | 'otp'

export default function MobileLogin() {
  const { user, loading, ended, signIn, requestOtp, verifyOtp } = useAuth()
  const { t } = useT()
  const navigate = useNavigate()
  const [method, setMethod] = useState<Method>('password')
  const [identifier, setIdentifier] = useState('')
  const [touched, setTouched] = useState(false)
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rejected, setRejected] = useState(false)
  const [busy, setBusy] = useState(false)
  const passwordRef = useRef<HTMLInputElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)

  /* one-time code state */
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [expiresMin, setExpiresMin] = useState(5)
  const [devCode, setDevCode] = useState<string | null>(null)
  const [resendAt, setResendAt] = useState(0)
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (resendAt <= Date.now()) return
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [resendAt])
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000))

  // Signed in already (or just now): straight to the app. A first sign-in on
  // a temporary password has to change it before anything else, and that
  // screen lives inside the mobile app rather than the desktop.
  useEffect(() => {
    if (!user) return
    if (user.mustChangePassword) { navigate('/m/change-password', { replace: true }); return }
    let welcomed = '1'
    // Storage throws in a private window and in some embedded webviews, and a
    // missing flag should never block a sign-in.
    try { welcomed = localStorage.getItem('clinic.m.welcomed') ?? '' } catch { welcomed = '1' }
    // Home is role-aware: each role lands on its own dashboard there.
    navigate(welcomed === '1' ? '/m' : '/m/welcome', { replace: true })
  }, [user, navigate])

  if (loading) {
    return (
      <div className="mapp-desk">
        <div className="mapp"><Loading /></div>
      </div>
    )
  }
  // The redirect lives in the effect above so it can consult the welcome flag;
  // an early Navigate here would always win the race and skip onboarding.

  const idError = rules.identifier(method === 'otp')(identifier)
  const showIdError = touched && !!idError

  function fail(err: unknown, fallback: string) {
    setError(err instanceof ApiFailure ? err.message : fallback)
  }

  function switchMethod(m: Method) {
    setMethod(m)
    setError(null)
    setRejected(false)
    setSentTo(null)
    setCode('')
    setDevCode(null)
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (idError) return
    setError(null)
    setBusy(true)
    try {
      await signIn(identifier.trim(), password)
      // The effect above redirects once the session lands.
    } catch (err) {
      fail(err, t('shell.couldNotSignIn'))
      // A password the browser filled in would otherwise sit there and be
      // resubmitted unchanged on every attempt, so the person never sees that
      // what they are sending is not what they think it is.
      const wrongCredential = err instanceof ApiFailure && err.code === 'INVALID_CREDENTIALS'
      setRejected(wrongCredential)
      if (wrongCredential) {
        setPassword('')
        // Focus after the re-render: clearing the value disables the submit
        // button, and React moves focus before a synchronous call would stick.
        setTimeout(() => passwordRef.current?.focus(), 0)
      }
      setBusy(false)
    }
  }

  async function sendCode(e?: FormEvent) {
    e?.preventDefault()
    setTouched(true)
    if (idError) return
    setError(null)
    setBusy(true)
    try {
      const r = await requestOtp(identifier.trim())
      setSentTo(identifier.trim())
      setExpiresMin(Math.round(r.expiresInSeconds / 60))
      setDevCode(r.devCode ?? null)
      setResendAt(Date.now() + r.resendInSeconds * 1000)
      setNow(Date.now())
      setCode('')
      setTimeout(() => codeRef.current?.focus(), 0)
    } catch (err) {
      fail(err, t('access.couldNotSend'))
      if (err instanceof ApiFailure && err.code === 'OTP_COOLDOWN') {
        const wait = Number(err.details.retryAfterSec ?? 30)
        setSentTo(identifier.trim())
        setResendAt(Date.now() + wait * 1000)
        setNow(Date.now())
      }
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault()
    if (!sentTo || !/^\d{6}$/.test(code)) return
    setError(null)
    setBusy(true)
    try {
      await verifyOtp(sentTo, code)
    } catch (err) {
      fail(err, t('shell.couldNotSignIn'))
      setCode('')
      setTimeout(() => codeRef.current?.focus(), 0)
      setBusy(false)
    }
  }

  const idField = (
    <div className={`m-field wide${showIdError ? ' has-err' : ''}`}>
      <label htmlFor="mu">{t('access.identifier')}</label>
      <input
        id="mu" autoFocus required autoCapitalize="none" autoCorrect="off" spellCheck={false}
        autoComplete="username" inputMode={method === 'otp' ? 'email' : 'text'}
        placeholder={t('access.identifierPh')}
        value={identifier} aria-invalid={showIdError || undefined}
        onChange={(e) => { setIdentifier(e.target.value); setError(null) }}
        onBlur={() => setTouched(!!identifier.trim())}
      />
      <FieldError>{showIdError ? idError : null}</FieldError>
      {method === 'password' && !showIdError && <span className="hint">{t('access.usernameStillWorks')}</span>}
    </div>
  )

  return (
    <div className="mapp-desk">
      <div className="mapp">
        <div className="mapp-screen no-tabs">
          <div className="m-login">
            <div className="m-login-mark"><Icon name="firstaid" size={30} /></div>
            <h1>{t('shell.signInHead', { app: 'Clinic Desk' })}</h1>
            {/* Patients sign in here too, so the line cannot assume the person
                reading it works at the clinic. */}
            <p className="m-note">{t('shell.signInSub')}</p>

            {ended && !error && (
              <div className="m-card m-note" role="status">
                {ended === 'expired' ? t('access.sessionExpired') : t('access.signedOut')}
              </div>
            )}

            <div role="alert" aria-live="assertive">
              {error && (
                <div className="m-alert-banner"><Icon name="alert" size={18} />{error}</div>
              )}
            </div>
            {rejected && (
              <p className="m-note" style={{ marginTop: -6 }}>
                {t('shell.passwordCleared')}
              </p>
            )}

            <div className="m-seg" role="tablist" aria-label={t('access.howToSignIn')}>
              <button type="button" role="tab" aria-selected={method === 'password'}
                className={method === 'password' ? 'is-on' : ''} onClick={() => switchMethod('password')}>
                {t('shell.password')}
              </button>
              <button type="button" role="tab" aria-selected={method === 'otp'}
                className={method === 'otp' ? 'is-on' : ''} onClick={() => switchMethod('otp')}>
                {t('access.oneTimeCode')}
              </button>
            </div>

            {method === 'password' && (
              <form className="m-form" noValidate onSubmit={(e) => void submitPassword(e)}>
                {idField}
                <div className="m-field wide">
                  <label htmlFor="mp">{t('shell.password')}</label>
                  <div className="m-pw">
                    <input
                      id="mp" ref={passwordRef} required type={show ? 'text' : 'password'}
                      autoComplete="current-password" value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button" className="m-pw-toggle"
                      aria-label={show ? t('shell.hidePassword') : t('shell.showPassword')}
                      onClick={() => setShow((v) => !v)}
                    >
                      <Icon name="eye" size={18} />
                    </button>
                  </div>
                </div>
                <button type="submit" className="m-btn block wide" disabled={busy || !identifier.trim() || !password}>
                  {busy ? t('shell.signingIn') : <>{t('shell.signIn')} <Icon name="chevron" size={18} /></>}
                </button>
              </form>
            )}

            {method === 'otp' && !sentTo && (
              <form className="m-form" noValidate onSubmit={(e) => void sendCode(e)}>
                {idField}
                <button type="submit" className="m-btn block wide" disabled={busy || !identifier.trim()}>
                  {busy ? t('access.sending') : t('access.sendCode')}
                </button>
              </form>
            )}

            {method === 'otp' && sentTo && (
              <form className="m-form" noValidate onSubmit={(e) => void submitCode(e)}>
                <p className="m-note wide" style={{ margin: 0 }}>
                  {t('access.codeSent', { to: sentTo, min: expiresMin })}
                </p>
                {devCode && (
                  <div className="m-card m-note wide" role="status">{t('access.devCode', { code: devCode })}</div>
                )}
                <div className="m-field wide">
                  <label htmlFor="mc">{t('access.codeLabel')}</label>
                  <input
                    id="mc" ref={codeRef} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                    pattern="\d{6}" value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null) }}
                  />
                </div>
                <button type="submit" className="m-btn block wide" disabled={busy || code.length !== 6}>
                  {busy ? t('shell.signingIn') : t('access.verifyAndSignIn')}
                </button>
                <div className="wide" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <button type="button" className="m-link" onClick={() => switchMethod('otp')}>
                    {t('access.changeIdentifier')}
                  </button>
                  <button type="button" className="m-link" disabled={busy || resendIn > 0} onClick={() => void sendCode()}>
                    {resendIn > 0 ? t('access.resendIn', { s: resendIn }) : t('access.resend')}
                  </button>
                </div>
              </form>
            )}

            <p className="m-note" style={{ textAlign: 'center' }}>
              {t('shell.accountsByAdmin')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
